// C:\Apps\Bresco\bresco-franquicies-frontend\src\forms\Siniestros\SiniestrosFormDetail.js
import { useModal } from 'contexts/ModalContext'
import { Field, Form, Formik } from 'formik'
import useFetch from 'hooks/useFetch'
import PropTypes from 'prop-types'
import { useRef, useState, useEffect } from 'react'
import { LuLock, LuPencil, LuRefreshCw, LuTrash2, LuHistory, LuMailCheck } from 'react-icons/lu'
import * as Yup from 'yup'

const yesNoOptions = [
  { value: '', label: '' },
  { value: 'NO', label: 'NO' },
  { value: 'SI', label: 'SI' }
]

const estadoSiniestroOptions = [
  { value: '', label: '' },
  { value: "ENVIADO CHUBB", label: "ENVIADO CHUBB" },
  { value: "PENDIENTE PAGO BA", label: "PENDIENTE PAGO BA" },
  { value: 'REVISADO', label: 'REVISADO' },
  { value: 'ACEPTADO CHUBB', label: 'ACEPTADO CHUBB' },
  { value: 'ENVIO FINIQUITO MRW', label: 'ENVIO FINIQUITO MRW' },
  { value: 'RECIBO FINIQUITO FIRMADO', label: 'RECIBO FINIQUITO FIRMADO' },
  { value: 'LIQUIDADO', label: 'LIQUIDADO' },
  { value: 'CERRADO', label: 'CERRADO' },
  { value: 'REVISIÓN POST CIERRE', label: 'REVISIÓN POST CIERRE' },
  { value: 'BAJO FRANQUICIA', label: 'BAJO FRANQUICIA' },
  { value: 'CANCELADO', label: 'CANCELADO' },
  { value: 'PENDIENTE DOC MRW', label: 'PENDIENTE DOC MRW' },
  { value: 'DOC PEDIDA', label: 'DOC PEDIDA' },
  { value: "NO TRAMITABLE", label: "NO TRAMITABLE" }
]

const estadoBrescoOptions = [
  { value: '', label: '' },
  { value: 'SO', label: 'SO' },
  { value: 'ACEPTADO chubb', label: 'ACEPTADO chubb' },
  { value: 'FINIQUITO echo', label: 'FINIQUITO echo' },
  { value: 'FINIQUITO enviado', label: 'FINIQUITO enviado' },
  { value: 'FINIQUITO firmado', label: 'FINIQUITO firmado' },
  { value: 'PEN. Pago', label: 'PEN. Pago' },
  { value: 'PAGADO', label: 'PAGADO' },
  { value: 'BAJO FRANQUICIA', label: 'BAJO FRANQUICIA' },
  { value: 'CANCELADO', label: 'CANCELADO' },
  { value: 'CERRADO', label: 'CERRADO' }
]

const formatDateForInput = (v) => {
  if (!v) return ''
  if (typeof v === 'string' && v.includes('T')) return v.split('T')[0]
  return String(v)
}

const formatDateTimeES = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  const dd = pad(d.getDate())
  const mm = pad(d.getMonth() + 1)
  const yyyy = d.getFullYear()
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${dd}/${mm}/${yyyy} a las ${hh}:${mi}`
}

const buildAuditText = (values) => {
  const createdRaw = values?.created_at
  const updatedRaw = values?.updated_at
  const created = formatDateTimeES(createdRaw)
  const updated = formatDateTimeES(updatedRaw)
  const who = String(values?.updated_by_full_name || '').trim()
  if (!created) return ''
  let sameTimestamp = false
  if (createdRaw && updatedRaw) {
    const c = new Date(createdRaw)
    const u = new Date(updatedRaw)
    if (!isNaN(c.getTime()) && !isNaN(u.getTime())) {
      sameTimestamp = c.getTime() === u.getTime()
    }
  }
  const neverEdited = !updated || !who || sameTimestamp
  if (neverEdited) return `Creado el ${created}.`
  return `Creado el ${created} y editado el día ${updated} por el usuario ${who}.`
}

const parseDocuments = (value) => {
  if (!value) return []
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((item) => {
        if (!item) return null
        if (typeof item === 'string') return { url: item, name: '' }
        if (typeof item === 'object') {
          const url =
            item.url || item.href || item.path ||
            item.file_url || item.document_url || item.location || ''
          const name = item.name || item.fileName || item.filename || item.originalname || ''
          if (typeof url === 'string' && url) {
            return { url, name: String(name || '') }
          }
        }
        return null
      })
      .filter(Boolean)
  } catch {
    return []
  }
}

const getDocumentName = (doc, internalId) => {
  const url = doc?.url
  if (!url) return ''
  if (doc?.name) return doc.name
  try {
    const basePrefix = 'https://bresco.gestinet.com/doc/'
    const idPrefix = internalId ? `${internalId}/` : ''
    const rawName = url?.split(basePrefix)[1]?.split(idPrefix)[1]
    if (rawName) return decodeURIComponent(rawName)
  } catch {
    // fallback
  }
  try {
    const pathname = new URL(url).pathname
    const fileName = pathname.split('/').filter(Boolean).pop()
    return decodeURIComponent(fileName || url)
  } catch {
    return url
  }
}

const getPreviewKind = (doc) => {
  const src = String(doc?.url || '')
  const name = String(doc?.name || '')
  if (src.startsWith('data:')) {
    const mime = src.slice(5).split(';')[0].toLowerCase()
    if (mime.startsWith('image/')) return 'image'
    if (mime === 'application/pdf') return 'pdf'
    return 'unsupported'
  }
  const base = (name || src).split('?')[0].split('#')[0].toLowerCase()
  const ext = base.includes('.') ? base.split('.').pop() : ''
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image'
  if (ext === 'pdf') return 'pdf'
  if (ext === 'docx' || ext === 'doc') return 'docx'
  return 'unsupported'
}

const getOfficePreviewUrl = (doc) => {
  const src = String(doc?.url || '')
  if (!/^https?:\/\//i.test(src)) return ''
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(src)}`
}

const SI_NO_KEYS = [
  'reclamacion', 'parte_extravio', 'albaran', 'factura_compra',
  'denuncia', 'factura_venta', 'expediente_completo', 'concepto_factura'
]

const normalizeInitialValues = (fields = {}) => {
  const out = { ...(fields || {}) }
  if (out.IDInterno === null || out.IDInterno === undefined) out.IDInterno = ''
  out.fecha_dia = formatDateForInput(out.fecha_dia)
  out.fecha_siniestro = formatDateForInput(out.fecha_siniestro)
  for (const k of SI_NO_KEYS) {
    if (out[k] === null || out[k] === undefined) out[k] = ''
    if (typeof out[k] === 'string') {
      const s = out[k].trim().toUpperCase()
      out[k] = s === 'SI' ? 'SI' : s === 'NO' ? 'NO' : ''
    }
  }
  const nullToEmpty = [
    'ref_fast', 'estado_siniestro', 'estado_bresco', 'img', 'img_extra',
    'comentarios', 'descripcion_mercancia', 'peso_mercancia_kg', 'margen_porcentaje',
    'franquicia_aplicable', 'importe_neto_cobrar', 'importe_reclamado',
    'tipo_pago', 'cuenta', 'concepto_factura'
  ]
  for (const k of nullToEmpty) {
    if (out[k] === null || out[k] === undefined) out[k] = ''
  }
  if (out.created_at === null || out.created_at === undefined) out.created_at = ''
  if (out.updated_at === null || out.updated_at === undefined) out.updated_at = ''
  if (out.updated_by_full_name === null || out.updated_by_full_name === undefined)
    out.updated_by_full_name = ''
  out.updated_by_full_name = String(out.updated_by_full_name || '').trim()
  return out
}

const inputBase = 'block w-full p-2 mt-2 border rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300'
const inputBaseWhite = 'block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300'

export default function SiniestrosFormDetail({
  isNew,
  user,
  fields,
  siniestroRef,
  handleSubmit,
  handleDelete
}) {
  const { openModal } = useModal()
  const apiBaseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || ''
  const fileInputRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)
  const [isReadingFiles, setIsReadingFiles] = useState(false)
  const [documentSearch, setDocumentSearch] = useState('')
  const [documentError, setDocumentError] = useState('')
  const [previewDocument, setPreviewDocument] = useState(null)

  const [savedFiles, saveFiles] = useFetch()
  const [deletedFile, deleteFile] = useFetch()
  const [refreshedDocs, refreshDocs] = useFetch()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshMessage, setRefreshMessage] = useState('')
  const [estadoLogs, fetchEstadoLogs] = useFetch()
  const [showHistorial, setShowHistorial] = useState(false)
  const [historialFiltroEstado, setHistorialFiltroEstado] = useState('')
  const [historialFiltroUsuario, setHistorialFiltroUsuario] = useState('')
  const [historialFiltroDesde, setHistorialFiltroDesde] = useState('')
  const [historialFiltroHasta, setHistorialFiltroHasta] = useState('')
  const [historialFiltroDelegacion, setHistorialFiltroDelegacion] = useState('')

  // ── Ref para acceder a setFieldValue de Formik desde los useEffect ──
  const setFieldValueRef = useRef(null)

  // ── Reacciona al resultado del upload de documentos ──
  useEffect(() => {
    if (!savedFiles.isCalled) return
    setIsReadingFiles(false)
    if (savedFiles.error) {
      setDocumentError(
        typeof savedFiles.error === 'string'
          ? savedFiles.error
          : savedFiles.error?.message || 'Error al subir documentos'
      )
      return
    }
    if (savedFiles.data && setFieldValueRef.current) {
      const rawDocs =
        savedFiles.data?.documents ||
        savedFiles.data?.data?.documents ||
        []
      const docsArray = Array.isArray(rawDocs) ? rawDocs : []
      setFieldValueRef.current('img_extra', docsArray.length ? JSON.stringify(docsArray) : '')
    }
  }, [savedFiles.isCalled, savedFiles.data, savedFiles.error])

  // ── Reacciona al resultado del delete de documento ──
  useEffect(() => {
    if (!deletedFile.isCalled) return
    setDeleteConfirmDoc(null) // ← cerrar modal de confirmación siempre
    if (deletedFile.error) {
      setDocumentError(
        typeof deletedFile.error === 'string'
          ? deletedFile.error
          : deletedFile.error?.message || 'Error al eliminar documento'
      )
      return
    }
    if (deletedFile.data && setFieldValueRef.current) {
      const rawDocs =
        deletedFile.data?.documents ||
        deletedFile.data?.data?.documents ||
        []
      const docsArray = Array.isArray(rawDocs) ? rawDocs : []
      setFieldValueRef.current('img_extra', docsArray.length ? JSON.stringify(docsArray) : '')
    }
  }, [deletedFile.isCalled, deletedFile.data, deletedFile.error])

  // Reacciona al resultado del refresco de documentos
  // Reacciona al resultado del refresco de documentos
  useEffect(() => {
    if (!refreshedDocs.isCalled) return
    setIsRefreshing(false)
    setRefreshMessage('')
    if (refreshedDocs.error) {
      setDocumentError(
        typeof refreshedDocs.error === 'string'
          ? refreshedDocs.error
          : refreshedDocs.error?.message || 'Error al refrescar documentos'
      )
      return
    }
    if (refreshedDocs.data && setFieldValueRef.current) {
      const rawDocs =
        refreshedDocs.data?.documents ||
        refreshedDocs.data?.data?.documents ||
        []
      const docsArray = Array.isArray(rawDocs) ? rawDocs : []
      setFieldValueRef.current('img_extra', docsArray.length ? JSON.stringify(docsArray) : '')
      const newFound = refreshedDocs.data?.new_found ?? refreshedDocs.data?.data?.new_found ?? 0
      setRefreshMessage(
        newFound > 0
          ? `Se han encontrado ${newFound} archivo${newFound !== 1 ? 's' : ''} nuevo${newFound !== 1 ? 's' : ''}.`
          : 'No se han encontrado archivos nuevos.'
      )
    }
  }, [refreshedDocs.isCalled, refreshedDocs.data, refreshedDocs.error])

  useEffect(() => {
    if (!isNew && fields?.id) {
      fetchEstadoLogs({
        url: `siniestros/${fields.id}/estado-logs`,
        method: 'GET',
      })
    }
  }, [isNew, fields?.id])

  // Estado para el modal de confirmación de borrado de documento
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState(null) // doc row a eliminar

  const [unlocked, setUnlocked] = useState(false)
  const [showUnlockModal, setShowUnlockModal] = useState(false)

  const validationSchema = Yup.object().shape({
    ref_albaran: Yup.string().nullable(),
    estado_siniestro: Yup.string().nullable(),
    fecha_dia: Yup.string().nullable(),
    fecha_siniestro: Yup.string().nullable(),
    importe_reclamado: Yup.number().nullable().typeError('Debe ser un número'),
    franquicia_aplicable: Yup.number().nullable().typeError('Debe ser un número'),
    importe_neto_cobrar: Yup.number().nullable().typeError('Debe ser un número'),
    margen_porcentaje: Yup.number().nullable().typeError('Debe ser un número'),
    peso_mercancia_kg: Yup.number().nullable().typeError('Debe ser un número')
  })

  return (
    <Formik
      enableReinitialize
      initialValues={normalizeInitialValues(fields)}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        await handleSubmit(values)
      }}
    >
      {({ values, errors, touched, setFieldValue }) => {
        setFieldValueRef.current = setFieldValue

        const finiquitoEnviadoAt = values?.finiquito_enviado_at
        const auditText = !isNew ? buildAuditText(values) : ''
        const isAdmin = Number(user?.role_id) === 1 || Number(user?.role_id) === 2

        const isRole3 = Number(user?.role_id) === 3
        const estadoActualUpper = String(fields?.estado_siniestro || '').trim().toUpperCase()
        const role3CanEdit = isRole3 && estadoActualUpper === 'REVISADO'

        const canDelete = Number(user?.role_id) === 1
        const estadoActual = String(fields?.estado_siniestro || '').trim().toUpperCase()
        const isCerrado = estadoActual === 'CERRADO' || estadoActual === 'LIQUIDADO'
        const cerradoLock = isCerrado && !unlocked

        const baseDocuments = parseDocuments(values.img)
        const extraDocuments = parseDocuments(values.img_extra)
        const documentRows = [
          ...baseDocuments.map((doc, index) => ({
            key: `base-${doc.url}-${index}`,
            ...doc,
            editable: false
          })),
          ...extraDocuments.map((doc, index) => ({
            key: `extra-${doc.url}-${index}`,
            ...doc,
            editable: true,
            extraIndex: index
          }))
        ]

        const searchTerm = documentSearch.trim().toLowerCase()
        const filteredDocumentRows = searchTerm
          ? documentRows.filter((row) => {
              const name = getDocumentName(row, values?.IDInterno).toLowerCase()
              const url = String(row.url || '').toLowerCase()
              return name.includes(searchTerm) || url.includes(searchTerm)
            })
          : documentRows

        const resolveUserId = () => {
          let userId = Number(user?.id)
          if (!userId || Number.isNaN(userId)) {
            try {
              const rawUser = localStorage.getItem('user')
              const parsedUser = rawUser ? JSON.parse(rawUser) : null
              userId = Number(parsedUser?.id)
            } catch {
              // ignore
            }
          }
          return userId
        }

        // ── Upload de documentos ──
        const addFilesToExtraDocuments = async (filesList) => {
          if (isNew) return
          const files = Array.from(filesList || []).filter(Boolean)
          if (!files.length) return
          const siniestroId = Number(values?.id)
          if (!siniestroId || Number.isNaN(siniestroId)) {
            setDocumentError('Debes guardar el siniestro antes de subir documentos.')
            return
          }
          const userId = resolveUserId()
          if (!userId || Number.isNaN(userId)) {
            setDocumentError('No se ha podido identificar el usuario de sesion.')
            return
          }
          if (!apiBaseUrl) {
            setDocumentError('No se ha configurado la URL de la API.')
            return
          }
          setDocumentError('')
          setIsReadingFiles(true)
          const formData = new FormData()
          files.forEach((file) => formData.append('documents', file))
          await saveFiles({
            url: `siniestros/${userId}/${siniestroId}/documents`,
            method: 'POST',
            formData
          })
        }

        // ── Borrar documento ──
        const deleteDocument = async (row) => {
          if (isNew || !row?.editable) return
          const siniestroId = Number(values?.id)
          if (!siniestroId || Number.isNaN(siniestroId)) {
            setDocumentError('No se pudo identificar el siniestro.')
            return
          }
          const userId = resolveUserId()
          if (!userId || Number.isNaN(userId)) {
            setDocumentError('No se ha podido identificar el usuario de sesion.')
            return
          }
          if (!apiBaseUrl) {
            setDocumentError('No se ha configurado la URL de la API.')
            return
          }
          try {
            setDocumentError('')
            await deleteFile({
              url: `siniestros/${userId}/${siniestroId}/document`,
              method: 'DELETE',
              body: {
                documentUrl: row.url,
                IDInterno: values?.IDInterno || ''
              }
            })
          } catch {
            setDocumentError('No se pudo eliminar el documento.')
          }
        }

        // ✅ NUEVO: Refrescar documentos desde SFTP
        const handleRefreshDocuments = async () => {
          const siniestroId = Number(values?.id)
          const userId = resolveUserId()
          if (!siniestroId || Number.isNaN(siniestroId)) {
            setDocumentError('No se pudo identificar el siniestro.')
            return
          }
          if (!userId || Number.isNaN(userId)) {
            setDocumentError('No se ha podido identificar el usuario de sesion.')
            return
          }
          if (!apiBaseUrl) {
            setDocumentError('No se ha configurado la URL de la API.')
            return
          }
          setDocumentError('')
          setIsRefreshing(true)
          await refreshDocs({
            url: `siniestros/${userId}/${siniestroId}/refresh-documents`,
            method: 'PUT',
          })
        }

        // ── Opciones select estado para role 3 ──
        const estadoSiniestroOptionsRole3 = [
          { value: '', label: '' },
          ...(estadoActualUpper && estadoActualUpper !== 'ACEPTADO CHUBB'
            ? [{ value: estadoActualUpper, label: estadoActualUpper, disabled: true }]
            : []
          ),
          { value: 'ACEPTADO CHUBB', label: 'ACEPTADO CHUBB' }
        ]

        return (
          <Form
            ref={siniestroRef}
            className="flex flex-col w-full p-6 mx-auto mt-6 bg-white border border-gray-200 rounded-md dark:border-gray-700 dark:bg-gray-800 gap-y-4"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                e.preventDefault()
              }
            }}
          >
            {/* Banner CERRADO — solo para admin, role3 no ve este banner */}
            {isCerrado && !isRole3 && (
              <div className={`flex items-center justify-between px-4 py-3 rounded-md border ${
                unlocked
                  ? 'bg-amber-50 border-amber-300 text-amber-800'
                  : 'bg-gray-100 border-gray-300 text-gray-600 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
              }`}>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <LuLock size={16} />
                  {unlocked
                    ? 'Modo edición activado. Guarda para confirmar los cambios.'
                    : 'Este siniestro está CERRADO. Todos los campos son de solo lectura.'}
                </div>
                {isAdmin && !unlocked && (
                  <button
                    type="button"
                    onClick={() => setShowUnlockModal(true)}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-white bg-[#FCAE52] border border-[#FCAE52] rounded-lg hover:bg-[#e99d3f] transition-colors"
                  >
                    <LuPencil size={14} />
                    Editar siniestro cerrado
                  </button>
                )}
              </div>
            )}
            {!isNew && finiquitoEnviadoAt && (
              <div className="flex items-center gap-2 px-4 py-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md dark:bg-green-900/20 dark:border-green-800 dark:text-green-400">
                <LuMailCheck size={16} />
                Finiquito enviado el {formatDateTimeES(finiquitoEnviadoAt)}
              </div>
            )}

            {/* Banner informativo para role 3 cuando NO puede editar */}
            {isRole3 && !role3CanEdit && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-md border bg-gray-100 border-gray-300 text-gray-600 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300">
                <LuLock size={16} />
                <span className="text-sm font-semibold">
                  Solo puedes editar siniestros en estado <strong>REVISADO</strong>. Este siniestro es de solo lectura.
                </span>
              </div>
            )}

            {/* Modal confirmación desbloqueo */}
            {showUnlockModal && (
              <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div className="w-full max-w-sm p-6 bg-white border-2 border-[#FCAE52] rounded-2xl shadow-xl dark:bg-gray-800 dark:border-[#FCAE52]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-[3px] border-[#FCAE52]">
                      <LuPencil className="text-gray-700 dark:text-gray-200" size={20} />
                    </div>
                    <p className="text-lg font-semibold text-gray-800 dark:text-white">
                      Editar siniestro cerrado
                    </p>
                  </div>
                  <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">
                    Este siniestro está marcado como <strong>CERRADO</strong>. ¿Estás seguro de que quieres editarlo? Esta acción puede modificar datos finalizados.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowUnlockModal(false)}
                      className="h-10 px-5 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUnlocked(true)
                        setShowUnlockModal(false)
                        setFieldValue('estado_siniestro', 'REVISIÓN POST CIERRE')
                      }}
                      className="h-10 px-5 text-sm font-semibold text-white bg-[#FCAE52] border border-[#FCAE52] rounded-xl hover:bg-[#e99d3f]"
                    >
                      Sí, editar
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-12 gap-4">
              {/* ref_albaran */}
              <div className="col-span-12 md:col-span-6">
                <label
                  className={`${
                    touched.ref_albaran && errors.ref_albaran
                      ? 'text-red-700 dark:text-red-500'
                      : 'text-gray-700 dark:text-gray-200'
                  }`}
                  htmlFor="ref_albaran"
                >
                  Ref. albarán
                </label>
                <Field
                  id="ref_albaran"
                  name="ref_albaran"
                  type="text"
                  className={`${inputBaseWhite} ${
                    touched.ref_albaran && errors.ref_albaran
                      ? 'border-red-500 text-red-900 focus:ring-red-500'
                      : ''
                  }`}
                  disabled={cerradoLock || !isNew}
                />
                {touched.ref_albaran && errors.ref_albaran ? (
                  <p className="mt-1 text-sm text-red-600">{errors.ref_albaran}</p>
                ) : null}
              </div>

              {/* IDInterno */}
              <div className="col-span-12 md:col-span-6">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="IDInterno">
                  ID Interno
                </label>
                <Field
                  id="IDInterno"
                  name="IDInterno"
                  type="text"
                  className={inputBase}
                  disabled={cerradoLock || !isNew}
                />
              </div>

              {/* estado_siniestro */}
              <div className="col-span-12 md:col-span-6">
                <label
                  className={`${
                    touched.estado_siniestro && errors.estado_siniestro
                      ? 'text-red-700 dark:text-red-500'
                      : 'text-gray-700 dark:text-gray-200'
                  }`}
                  htmlFor="estado_siniestro"
                >
                  Estado siniestro
                </label>
                <Field
                  as="select"
                  id="estado_siniestro"
                  name="estado_siniestro"
                  className={`${inputBaseWhite} ${
                    touched.estado_siniestro && errors.estado_siniestro
                      ? 'border-red-500 text-red-900 focus:ring-red-500'
                      : ''
                  }`}
                  disabled={
                    isRole3
                      ? !role3CanEdit
                      : cerradoLock || !isAdmin
                  }
                >
                  {isRole3
                    ? estadoSiniestroOptionsRole3.map((o) => (
                        <option key={o.value} value={o.value} disabled={o.disabled || false}>
                          {o.label}
                        </option>
                      ))
                    : estadoSiniestroOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))
                  }
                </Field>
                {touched.estado_siniestro && errors.estado_siniestro ? (
                  <p className="mt-1 text-sm text-red-600">{errors.estado_siniestro}</p>
                ) : null}
              </div>

              {/* estado_bresco (oculto) */}
              <div className="col-span-12 md:col-span-6" style={{ display: 'none' }}>
                <label className="text-gray-700 dark:text-gray-200" htmlFor="estado_bresco">
                  Estado Bresco
                </label>
                <Field
                  as="select"
                  id="estado_bresco"
                  name="estado_bresco"
                  className={inputBaseWhite}
                  disabled={cerradoLock || !isAdmin}
                >
                  {estadoBrescoOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Field>
              </div>

              {/* fecha_dia */}
              <div className="col-span-12 md:col-span-3">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="fecha_dia">
                  Fecha día
                </label>
                <Field id="fecha_dia" name="fecha_dia" type="date" className={inputBaseWhite} disabled={cerradoLock || !isAdmin} />
              </div>

              {/* fecha_siniestro */}
              <div className="col-span-12 md:col-span-3">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="fecha_siniestro">
                  Fecha siniestro
                </label>
                <Field id="fecha_siniestro" name="fecha_siniestro" type="date" className={inputBase} disabled={cerradoLock || isRole3} />
              </div>

              {/* ref_fast */}
              <div className="col-span-12 md:col-span-3">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="ref_fast">Ref. FAST</label>
                <Field id="ref_fast" name="ref_fast" type="text" className={inputBaseWhite} disabled={cerradoLock || !isAdmin} />
              </div>

              {/* tipo_pago */}
              <div className="col-span-12 md:col-span-3">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="tipo_pago">Tipo pago</label>
                <Field id="tipo_pago" name="tipo_pago" type="text" className={inputBase} disabled={cerradoLock || !isNew} />
              </div>

              {/* causa */}
              <div className="col-span-12 md:col-span-6">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="causa">Causa</label>
                <Field id="causa" name="causa" type="text" className={inputBase} disabled={cerradoLock || !isNew} />
              </div>

              {/* delegacion_propietaria */}
              <div className="col-span-12 md:col-span-4">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="delegacion_propietaria">
                  Delegación propietaria
                </label>
                <Field id="delegacion_propietaria" name="delegacion_propietaria" type="text" className={inputBase} disabled={cerradoLock || !isNew} />
              </div>

              {/* nombre_franquiciado */}
              <div className="col-span-12 md:col-span-8">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="nombre_franquiciado">
                  Nombre franquiciado
                </label>
                <Field id="nombre_franquiciado" name="nombre_franquiciado" type="text" className={inputBase} disabled={cerradoLock || !isNew} />
              </div>

              {/* cuenta */}
              <div className="col-span-12 md:col-span-6">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="cuenta">Cuenta (IBAN)</label>
                <Field id="cuenta" name="cuenta" type="text" className={inputBase} disabled={cerradoLock || !isNew} />
              </div>

              {/* descripcion_mercancia */}
              <div className="col-span-12 md:col-span-6">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="descripcion_mercancia">
                  Descripción mercancía
                </label>
                <Field id="descripcion_mercancia" name="descripcion_mercancia" type="text" className={inputBaseWhite} disabled={cerradoLock || !isAdmin} />
              </div>

              {/* importe_reclamado */}
              <div className="col-span-12 md:col-span-4">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="importe_reclamado">
                  Importe reclamado
                </label>
                <Field id="importe_reclamado" name="importe_reclamado" type="text" className={inputBaseWhite} disabled={cerradoLock || !isAdmin} />
                {touched.importe_reclamado && errors.importe_reclamado ? (
                  <p className="mt-1 text-sm text-red-600">{errors.importe_reclamado}</p>
                ) : null}
              </div>

              {/* franquicia_aplicable */}
              <div className="col-span-12 md:col-span-4">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="franquicia_aplicable">
                  Franquicia aplicable
                </label>
                <Field id="franquicia_aplicable" name="franquicia_aplicable" type="text" className={inputBaseWhite} disabled={cerradoLock || !isAdmin} />
                {touched.franquicia_aplicable && errors.franquicia_aplicable ? (
                  <p className="mt-1 text-sm text-red-600">{errors.franquicia_aplicable}</p>
                ) : null}
              </div>

              {/* importe_neto_cobrar */}
              <div className="col-span-12 md:col-span-4">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="importe_neto_cobrar">
                  Importe neto a cobrar
                </label>
                <Field id="importe_neto_cobrar" name="importe_neto_cobrar" type="text" className={inputBaseWhite} disabled={cerradoLock || !isAdmin} />
                {touched.importe_neto_cobrar && errors.importe_neto_cobrar ? (
                  <p className="mt-1 text-sm text-red-600">{errors.importe_neto_cobrar}</p>
                ) : null}
              </div>

              {/* peso_mercancia_kg */}
              <div className="col-span-12 md:col-span-4">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="peso_mercancia_kg">
                  Peso mercancía (kg)
                </label>
                <Field id="peso_mercancia_kg" name="peso_mercancia_kg" type="text" className={inputBaseWhite} disabled={cerradoLock || !isAdmin} />
                {touched.peso_mercancia_kg && errors.peso_mercancia_kg ? (
                  <p className="mt-1 text-sm text-red-600">{errors.peso_mercancia_kg}</p>
                ) : null}
              </div>

              {/* margen_porcentaje */}
              <div className="col-span-12 md:col-span-4">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="margen_porcentaje">
                  Margen (%)
                </label>
                <Field id="margen_porcentaje" name="margen_porcentaje" type="text" className={inputBaseWhite} disabled={cerradoLock || !isAdmin} />
                {touched.margen_porcentaje && errors.margen_porcentaje ? (
                  <p className="mt-1 text-sm text-red-600">{errors.margen_porcentaje}</p>
                ) : null}
              </div>

              {/* comentarios */}
              <div className="col-span-12">
                <label className="text-gray-700 dark:text-gray-200" htmlFor="comentarios">
                  Comentarios
                </label>
                <Field
                  id="comentarios"
                  name="comentarios"
                  as="textarea"
                  className="block w-full h-auto p-2 mt-2 text-gray-700 bg-white border border-gray-200 rounded-sm min-h-11 max-h-52 focus:ring-opacity-40 focus:outline-none focus:ring disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300"
                  disabled={cerradoLock || !isAdmin}
                />
              </div>

              {/* Flags SI/NO */}
              <div className="col-span-12">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Documentación / expediente
                </p>
                <div className="grid grid-cols-12 gap-4 mt-2">
                  {[
                    ['concepto_factura', 'Concepto factura'],
                    ['reclamacion', 'Reclamación'],
                    ['parte_extravio', 'Parte extravío'],
                    ['albaran', 'Albarán'],
                    ['factura_compra', 'Factura compra'],
                    ['denuncia', 'Denuncia'],
                    ['factura_venta', 'Factura venta'],
                    ['expediente_completo', 'Expediente completo']
                  ].map(([key, label]) => (
                    <div key={key} className="col-span-12 sm:col-span-6 lg:col-span-3">
                      <label className="text-gray-700 dark:text-gray-200" htmlFor={key}>{label}</label>
                      <Field as="select" id={key} name={key} className={inputBaseWhite} disabled={cerradoLock || !isAdmin}>
                        {yesNoOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Field>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Documentos */}
            <div className="col-span-12">
              {/* ✅ Cabecera con label + botón refrescar (solo admins, solo en edición) */}
              <div className="flex items-center justify-between mb-1">
                <label className="text-gray-700 dark:text-gray-200">
                  Documentos
                </label>
                {isAdmin && !isNew && (
                  <button
                    type="button"
                    onClick={handleRefreshDocuments}
                    disabled={isRefreshing}
                    title="Buscar nuevos archivos en el servidor"
                    className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-[#FCAE52] border border-[#FCAE52] rounded-lg hover:bg-orange-50 disabled:opacity-40 disabled:cursor-not-allowed dark:hover:bg-gray-700 transition-colors"
                  >
                    <LuRefreshCw size={13} className={isRefreshing ? 'animate-spin' : ''} />
                    {isRefreshing ? 'Buscando...' : 'Refrescar documentos'}
                  </button>
                )}
              </div>

              <input
                type="text"
                value={documentSearch}
                onChange={(e) => setDocumentSearch(e.target.value)}
                placeholder="Buscar archivo..."
                className={inputBaseWhite}
              />
              <div className="mt-2 overflow-hidden border border-gray-200 rounded-sm dark:border-gray-600">
                <div className="relative h-64 overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900/40">
                      <tr>
                        <th className="sticky top-0 z-10 px-3 py-2 text-left text-gray-700 bg-gray-50 dark:text-gray-200 dark:bg-gray-900/40">
                          Archivo
                        </th>
                        <th className="sticky top-0 z-10 w-48 px-3 py-2 text-left text-gray-700 bg-gray-50 dark:text-gray-200 dark:bg-gray-900/40">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                      {filteredDocumentRows.length ? (
                        filteredDocumentRows.map((row) => (
                          <tr key={row.key}>
                            <td className="px-3 py-2 align-top">
                              <a
                                href={row.url}
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                                className="text-blue-600 underline break-all hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                {getDocumentName(row, values?.IDInterno)}
                              </a>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setPreviewDocument(row)}
                                  className="px-2 py-1 text-xs font-medium text-blue-600 border border-blue-300 rounded-sm hover:bg-blue-50 dark:text-blue-300 dark:border-blue-800 dark:hover:bg-blue-950/30"
                                >
                                  Vista previa
                                </button>
                                {row.editable && !isRole3 ? (
                                  <button
                                    type="button"
                                    onClick={() => setDeleteConfirmDoc(row)}
                                    disabled={isNew}
                                    className="px-2 py-1 text-xs font-medium text-red-600 border border-red-300 rounded-sm hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed dark:text-red-300 dark:border-red-800 dark:hover:bg-red-950/30"
                                  >
                                    Eliminar
                                  </button>
                                ) : row.editable && isRole3 ? null : (
                                  <span className="text-xs text-gray-400 dark:text-gray-500">Solo lectura</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={2} className="px-3 py-3 text-gray-500 dark:text-gray-400">
                            {documentRows.length
                              ? 'No hay resultados para la busqueda.'
                              : 'No hay documentos disponibles.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Zona drag & drop — oculta para role 3 */}
              {!isRole3 && (
                <div className="mt-3">
                  <input
                    ref={fileInputRef}
                    id="img_extra"
                    type="file"
                    multiple
                    className="hidden"
                    onChange={async (e) => {
                      await addFilesToExtraDocuments(e.target.files)
                      e.target.value = ''
                    }}
                    disabled={isNew}
                  />
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => { if (isNew) return; fileInputRef.current?.click() }}
                    onKeyDown={(e) => {
                      if (isNew) return
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click() }
                    }}
                    onDragOver={(e) => { e.preventDefault(); if (isNew) return; setDragOver(true) }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={async (e) => {
                      e.preventDefault()
                      setDragOver(false)
                      await addFilesToExtraDocuments(e.dataTransfer?.files)
                    }}
                    className={`w-full p-4 text-center border border-dashed rounded-sm transition-colors ${
                      isNew
                        ? 'opacity-50 cursor-not-allowed border-gray-300 text-gray-400 dark:border-gray-700 dark:text-gray-500'
                        : dragOver
                          ? 'cursor-pointer border-[#FCAE52] bg-orange-50 text-gray-700 dark:bg-gray-900/50 dark:text-gray-200'
                          : 'cursor-pointer border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700/40'
                    }`}
                  >
                    {isReadingFiles
                      ? 'Procesando archivos...'
                      : 'Arrastra archivos aqui o haz clic para abrir el explorador'}
                  </div>
                  {isNew ? (
                    <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                      Para anadir documentos, primero debes guardar el siniestro.
                    </p>
                  ) : null}
                </div>
              )}

              {refreshMessage && !documentError ? (
                <p className="mt-1 text-sm text-green-600 dark:text-green-400">{refreshMessage}</p>
              ) : null}
              {documentError ? (
                <p className="mt-1 text-sm text-red-600">{documentError}</p>
              ) : null}
            </div>

            {/* Historial de modificaciones de estado */}
            {!isNew && !isRole3 && (
              <div className="col-span-12">
                <button
                  type="button"
                  onClick={() => setShowHistorial((v) => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-[#FCAE52] dark:hover:text-[#FCAE52] transition-colors"
                >
                  <LuHistory size={16} />
                  {showHistorial ? 'Ocultar historial de estados' : 'Ver historial de estados'}
                  <span className="ml-1 px-1.5 py-0.5 text-xs font-bold bg-gray-100 dark:bg-gray-700 rounded-full text-gray-500 dark:text-gray-400">
                    {Array.isArray(estadoLogs?.data) ? estadoLogs.data.length : 0}
                  </span>
                </button>

                {showHistorial && (
                  <div className="mt-3">
                    {estadoLogs?.loading ? (
                      <div className="flex items-center justify-center py-6 text-sm text-gray-500 dark:text-gray-400">
                        Cargando historial...
                      </div>
                    ) : estadoLogs?.error ? (
                      <div className="px-4 py-3 text-sm text-red-600 dark:text-red-400">
                        Error cargando el historial.
                      </div>
                    ) : !Array.isArray(estadoLogs?.data) || estadoLogs.data.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 border border-gray-200 rounded-sm dark:border-gray-600">
                        No hay cambios de estado registrados.
                      </div>
                    ) : (() => {
                      // ── Opciones únicas para los selects ──
                      const logData = estadoLogs.data
                      const estadoOpts = [...new Set(logData.map((l) => l.estado).filter(Boolean))].sort()
                      const usuarioOpts = [...new Set(logData.map((l) => String(l.changed_by_full_name || '').trim()).filter(Boolean))].sort()
                      const delegacionOpts = [...new Set(logData.map((l) => String(l.delegacion_propietaria || '').trim()).filter(Boolean))].sort()

                      // ── Filtros (leídos desde state, definidos justo debajo del componente) ──
                      const filtered = logData
                        .filter((l) => {
                          if (historialFiltroEstado && l.estado !== historialFiltroEstado) return false
                          if (historialFiltroUsuario && String(l.changed_by_full_name || '').trim() !== historialFiltroUsuario) return false
                          if (historialFiltroDelegacion && String(l.delegacion_propietaria || '').trim() !== historialFiltroDelegacion) return false
                          if (historialFiltroDesde) {
                            const d = new Date(l.changed_at)
                            const desde = new Date(historialFiltroDesde)
                            if (d < desde) return false
                          }
                          if (historialFiltroHasta) {
                            const d = new Date(l.changed_at)
                            const hasta = new Date(historialFiltroHasta)
                            hasta.setHours(23, 59, 59, 999)
                            if (d > hasta) return false
                          }
                          return true
                        })
                        .sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at))

                      return (
                        <>
                          {/* Filtros */}
                          <div className="grid grid-cols-12 gap-2 mb-2">
                            <div className="col-span-12 sm:col-span-3">
                              <select
                                value={historialFiltroEstado}
                                onChange={(e) => setHistorialFiltroEstado(e.target.value)}
                                className="block w-full p-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded-sm dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:outline-none"
                              >
                                <option value="">Todos los estados</option>
                                {estadoOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                              </select>
                            </div>
                            <div className="col-span-12 sm:col-span-3">
                              <select
                                value={historialFiltroUsuario}
                                onChange={(e) => setHistorialFiltroUsuario(e.target.value)}
                                className="block w-full p-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded-sm dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:outline-none"
                              >
                                <option value="">Todos los usuarios</option>
                                {usuarioOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                              </select>
                            </div>
                            <div className="col-span-12 sm:col-span-2">
                              <input
                                type="date"
                                value={historialFiltroDesde}
                                onChange={(e) => setHistorialFiltroDesde(e.target.value)}
                                className="block w-full p-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded-sm dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:outline-none"
                                placeholder="Desde"
                              />
                            </div>
                            <div className="col-span-12 sm:col-span-2">
                              <input
                                type="date"
                                value={historialFiltroHasta}
                                onChange={(e) => setHistorialFiltroHasta(e.target.value)}
                                className="block w-full p-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded-sm dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:outline-none"
                                placeholder="Hasta"
                              />
                            </div>
                            <div className="hidden">
                              <select
                                value={historialFiltroDelegacion}
                                onChange={(e) => setHistorialFiltroDelegacion(e.target.value)}
                              >
                                <option value="">Todas las delegaciones</option>
                                {delegacionOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                              </select>
                            </div>
                            <div className="col-span-12 sm:col-span-2 flex items-center">
                              <button
                                type="button"
                                onClick={() => {
                                  setHistorialFiltroEstado('')
                                  setHistorialFiltroUsuario('')
                                  setHistorialFiltroDesde('')
                                  setHistorialFiltroHasta('')
                                  setHistorialFiltroDelegacion('')
                                }}
                                className="w-full p-1.5 text-xs text-gray-500 border border-gray-200 rounded-sm hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
                              >
                                Limpiar filtros
                              </button>
                            </div>
                          </div>

                          {/* Tabla */}
                          <div className="overflow-hidden border border-gray-200 rounded-sm dark:border-gray-600">
                            <div className="relative h-64 overflow-auto">
                              {filtered.length === 0 ? (
                                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                  No hay registros que coincidan con los filtros.
                                </div>
                              ) : (
                                <table className="min-w-full text-sm">
                                  <thead className="bg-gray-50 dark:bg-gray-900/40">
                                    <tr>
                                      <th className="sticky top-0 z-10 px-3 py-2 text-left text-xs font-semibold text-gray-600 bg-gray-50 dark:text-gray-300 dark:bg-gray-900/40 uppercase tracking-wide">
                                        Fecha
                                      </th>
                                      <th className="sticky top-0 z-10 px-3 py-2 text-left text-xs font-semibold text-gray-600 bg-gray-50 dark:text-gray-300 dark:bg-gray-900/40 uppercase tracking-wide">
                                        Estado
                                      </th>
                                      <th className="sticky top-0 z-10 px-3 py-2 text-left text-xs font-semibold text-gray-600 bg-gray-50 dark:text-gray-300 dark:bg-gray-900/40 uppercase tracking-wide hidden">
                                        Delegación
                                      </th>
                                      <th className="sticky top-0 z-10 px-3 py-2 text-left text-xs font-semibold text-gray-600 bg-gray-50 dark:text-gray-300 dark:bg-gray-900/40 uppercase tracking-wide">
                                        Usuario
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-100 dark:bg-gray-800 dark:divide-gray-700">
                                    {filtered.map((log, idx) => {
                                      const isFirst = idx === 0
                                      const fecha = (() => {
                                        if (!log.changed_at) return '—'
                                        const d = new Date(log.changed_at)
                                        if (isNaN(d.getTime())) return String(log.changed_at)
                                        const pad = (n) => String(n).padStart(2, '0')
                                        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
                                      })()
                                      return (
                                        <tr key={log.id ?? idx} className={isFirst ? 'bg-amber-50 dark:bg-amber-900/10' : ''}>
                                          <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                            {fecha}
                                          </td>
                                          <td className="px-3 py-2">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                              isFirst
                                                ? 'bg-[#FCAE52]/20 text-[#b87d2a] dark:text-[#FCAE52]'
                                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                                            }`}>
                                              {log.estado || '—'}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400 hidden">
                                            {log.delegacion_propietaria || '—'}
                                          </td>
                                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                            {String(log.changed_by_full_name || '').trim() || '—'}
                                          </td>
                                        </tr>
                                      )
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Footer: Auditoría + Botones */}
            {!isNew ? (
              <div className="flex flex-col gap-3 pt-6 mt-2 border-t border-gray-200 md:flex-row md:items-center md:justify-between dark:border-gray-700">
                <div className="min-w-0">
                  {auditText ? (
                    <p className="text-sm font-bold text-gray-700 break-words dark:text-gray-200">
                      {auditText}
                    </p>
                  ) : null}
                </div>

                {/* Botones admin (role 1 y 2) */}
                {isAdmin && !cerradoLock ? (
                  <div className="flex justify-end gap-x-4">
                    {canDelete ? (
                      <button
                        type="button"
                        onClick={() =>
                          openModal('DeleteModal', {
                            itemName: values.ref_albaran ?? values.id ?? 'siniestro',
                            itemId: values.id,
                            handleDelete
                          })
                        }
                        className="w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-red-500 border border-red-500 rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-red-500 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-40 focus:outline-none focus:ring focus:ring-red-500 focus:border-red-500 dark:focus:border-red-500"
                      >
                        Eliminar
                      </button>
                    ) : null}
                    <button
                      type="submit"
                      className="w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-[#FCAE52] border border-[#FCAE52] rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-[#FCAE52] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-40 focus:outline-none focus:ring focus:ring-[#FCAE52] focus:border-[#FCAE52] dark:focus:border-[#FCAE52]"
                    >
                      Guardar
                    </button>
                  </div>
                ) : null}

                {/* Botón guardar para role 3 — solo si puede editar */}
                {role3CanEdit ? (
                  <div className="flex justify-end gap-x-4">
                    <button
                      type="submit"
                      className="w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-[#FCAE52] border border-[#FCAE52] rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-[#FCAE52] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-40 focus:outline-none focus:ring focus:ring-[#FCAE52] focus:border-[#FCAE52] dark:focus:border-[#FCAE52]"
                    >
                      Guardar
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              isAdmin ? (
                <div className="flex justify-end pt-6 mt-2 border-t border-gray-200 gap-x-4 dark:border-gray-700">
                  <button
                    type="submit"
                    className="w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-[#FCAE52] border border-[#FCAE52] rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-[#FCAE52] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-40 focus:outline-none focus:ring focus:ring-[#FCAE52] focus:border-[#FCAE52] dark:focus:border-[#FCAE52]"
                  >
                    Guardar
                  </button>
                </div>
              ) : null
            )}

            {/* ✅ Modal confirmación borrado de documento */}
            {deleteConfirmDoc ? (
              <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div className="w-full max-w-sm p-6 bg-white border-2 border-red-400 rounded-2xl shadow-xl dark:bg-gray-800 dark:border-red-500">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-[3px] border-red-400 dark:border-red-500">
                      <LuTrash2 className="text-red-500 dark:text-red-400" size={20} />
                    </div>
                    <p className="text-lg font-semibold text-gray-800 dark:text-white">
                      Eliminar documento
                    </p>
                  </div>
                  <p className="mb-1 text-sm text-gray-700 dark:text-gray-200">
                    ¿Estás seguro de que quieres eliminar el archivo:
                  </p>
                  <p className="mb-4 text-sm font-semibold text-gray-900 break-all dark:text-white">
                    «{getDocumentName(deleteConfirmDoc, values?.IDInterno)}»
                  </p>
                  <p className="mb-6 text-xs text-red-600 dark:text-red-400 font-medium">
                    Esta acción es irreversible y eliminará el archivo del servidor permanentemente.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmDoc(null)}
                      className="h-10 px-5 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={async () => { await deleteDocument(deleteConfirmDoc) }}
                      className="h-10 px-5 text-sm font-semibold text-white bg-red-500 border border-red-500 rounded-xl hover:bg-red-600"
                    >
                      Sí, eliminar
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Modal vista previa documento */}
            {previewDocument ? (
              <div className="fixed inset-0 z-20 flex items-center justify-center p-4 bg-black/50">
                <div className="w-full max-w-5xl overflow-hidden bg-white border border-gray-200 rounded-sm dark:bg-gray-900 dark:border-gray-700">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                      {getDocumentName(previewDocument, values?.IDInterno)}
                    </p>
                    <button
                      type="button"
                      onClick={() => setPreviewDocument(null)}
                      className="px-3 py-1 text-xs font-medium text-gray-700 border border-gray-300 rounded-sm hover:bg-gray-50 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-800"
                    >
                      Cerrar
                    </button>
                  </div>
                  <div className="h-[70vh] p-4">
                    {getPreviewKind(previewDocument) === 'image' ? (
                      <img
                        src={previewDocument.url}
                        alt={getDocumentName(previewDocument, values?.IDInterno)}
                        className="object-contain w-full h-full"
                      />
                    ) : getPreviewKind(previewDocument) === 'pdf' ? (
                      <iframe
                        title={getDocumentName(previewDocument, values?.IDInterno)}
                        src={previewDocument.url}
                        className="w-full h-full border border-gray-200 dark:border-gray-700"
                      />
                    ) : getPreviewKind(previewDocument) === 'docx' ? (
                      getOfficePreviewUrl(previewDocument) ? (
                        <iframe
                          title={getDocumentName(previewDocument, values?.IDInterno)}
                          src={getOfficePreviewUrl(previewDocument)}
                          className="w-full h-full border border-gray-200 dark:border-gray-700"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center w-full h-full gap-3 text-center">
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            Para vista previa de DOCX se necesita una URL publica (http/https).
                          </p>
                          <a
                            href={previewDocument.url}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-sm hover:bg-blue-50 dark:text-blue-300 dark:border-blue-800 dark:hover:bg-blue-950/30"
                          >
                            Abrir en nueva pestaña
                          </a>
                        </div>
                      )
                    ) : (
                      <div className="flex flex-col items-center justify-center w-full h-full gap-3 text-center">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Este tipo de archivo no tiene vista previa embebida.
                        </p>
                        <a
                          href={previewDocument.url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-sm hover:bg-blue-50 dark:text-blue-300 dark:border-blue-800 dark:hover:bg-blue-950/30"
                        >
                          Abrir en nueva pestaña
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </Form>
        )
      }}
    </Formik>
  )
}

SiniestrosFormDetail.propTypes = {
  isNew: PropTypes.bool,
  user: PropTypes.object,
  fields: PropTypes.object,
  siniestroRef: PropTypes.any,
  handleSubmit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired
}